import React from "react";
import { T } from "services/lang/LanguageService";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { confirmPopup } from "primereact/confirmpopup";
import { DatasetScatterProps, DiscreteValue } from "../interfaces";
import { getHexRandomColor } from "../utils";

interface DatasetScatterChartColorEditorProps {
  config: DatasetScatterProps;
  onConfigChange: (config: DatasetScatterProps) => void;
}

const DatasetScatterChartColorEditor = ({ config, onConfigChange }: DatasetScatterChartColorEditorProps) => {
  const handleChange = (field: keyof DatasetScatterProps, value: string | boolean) => {
    onConfigChange({ ...config, [field]: value });
  };

  const addDiscreteValue = () => {
    const existingKeys = new Set(config.discreteValues?.map(obj => obj.key) ?? []);
    let newKey = 0;
    while (existingKeys.has(newKey)) {
      newKey++;
    }
    onConfigChange({
      ...config,
      discreteValues: [
        ...(config.discreteValues ?? []),
        { key: newKey, value: getHexRandomColor(), title: `Category ${newKey}` },
      ],
    });
  };

  const valueEditorTemplate = (options: ColumnEditorOptions) => (
    <InputNumber
      value={options.value}
      onValueChange={e => options.editorCallback?.(e.value)}
      className="w-full"
    />
  );

  const titleEditorTemplate = (options: ColumnEditorOptions) => (
    <InputText
      type="text"
      value={options.value}
      onChange={e => options.editorCallback?.(e.target.value)}
      className="w-full p-inputtext"
    />
  );

  const colorEditorTemplate = (options: ColumnEditorOptions) => (
    <ColorPicker value={options.value} onValueChange={e => options.editorCallback?.(e.target.value)} />
  );

  const colorBodyTemplate = (rowData: DiscreteValue) => (
    <div
      style={{
        width: "25px",
        height: "25px",
        backgroundColor: rowData.value,
        borderRadius: "5px",
        border: "1px solid #c8c8c8",
      }}
    />
  );

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData, index } = e;

    if (!config.discreteValues || index >= config.discreteValues.length) {
      return;
    }

    const existingKeys = new Set(config.discreteValues.map(obj => obj.key));
    const currentKey = config.discreteValues[index].key;
    existingKeys.delete(currentKey);

    if (existingKeys.has(newData.key)) {
      easasAlert({
        summary: T("alert.discrete-value-already-exists", { value: newData.key }),
        severity: "warn",
      });
      return;
    }

    const updatedDiscreteValues = [...config.discreteValues];
    updatedDiscreteValues[index] = newData as DiscreteValue;

    onConfigChange({
      ...config,
      discreteValues: updatedDiscreteValues,
    });
  };

  const removeDiscreteValueTemplate = (rowData: DiscreteValue) => (
    <Button
      icon="pi pi-times"
      className="p-button-sm p-button-text p-button-danger"
      tooltip={T("action.remove-discrete-value")}
      onClick={event =>
        confirmPopup({
          target: event.currentTarget,
          message: T("message.are-you-sure-you-want-to-delete-this-discrete-value"),
          icon: "pi pi-exclamation-triangle",
          accept: () => {
            onConfigChange({
              ...config,
              discreteValues: config.discreteValues?.filter(obj => obj.key !== rowData.key) ?? [],
            });
          },
        })
      }
    />
  );

  const addButtonTemplate = () => {
    return (
      <Button
        icon="pi pi-plus"
        className="p-button-sm p-button-text p-button-success"
        tooltip={T("action.add-discrete-value")}
        onClick={addDiscreteValue}
      />
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-row align-items-center gap-2">
        <label htmlFor="discreteColorMode">{T("action.use-discrete-colors")}</label>
        <InputSwitch
          checked={Boolean(config?.discreteColorMode)}
          onChange={e => handleChange("discreteColorMode", e.value)}
          disabled={!config.colorAxisIdentifier}
        />
      </div>
      <div className="flex flex-column gap-2 w-full">
        <DataTable
          value={config.discreteValues}
          size="small"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
        >
          <Column
            field="key"
            header={T("word.value")}
            editor={valueEditorTemplate}
            style={{ width: "15%" }}
          />
          <Column
            field="title"
            header={T("word.title")}
            editor={titleEditorTemplate}
            style={{ width: "35%" }}
          />
          <Column
            field="value"
            header={T("word.color")}
            editor={colorEditorTemplate}
            body={colorBodyTemplate}
            style={{ width: "20%" }}
          />
          <Column rowEditor style={{ width: "20%", textAlign: "end" }} />
          <Column body={removeDiscreteValueTemplate} style={{ width: "10%" }} header={addButtonTemplate} />
        </DataTable>
      </div>
    </div>
  );
};

export default DatasetScatterChartColorEditor;
